'use client';

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { ChatInterface } from '@/components/interview/ChatInterface';
import { PreChatForm } from '@/components/interview/PreChatForm';
import {
  getStoredSession,
  recoverSession,
  clearStoredSession,
  findCompletedSessionByEmail,
} from '@/lib/interview/session-recovery';
import type { InterviewSession } from '@/lib/interview/types';
import type { CompletedSessionRecord } from '@/lib/interview/session-recovery';
import { withBase } from '@/lib/base-path'
import { pushEvent, pushPageContext } from '@/lib/tracking'

const FLOW_TYPE = 'client' as const;

interface RecoveredMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

export default function IntakePage() {
  const searchParams = useSearchParams();

  const referralSource = searchParams.get('ref') || undefined;
  const referralCampaign = searchParams.get('campaign') || undefined;
  const forceNew = searchParams.get('new') === 'true';
  // Deeplink from the spaik.io chat widget — when both params are present
  // we promote the light session to full intake and resume immediately,
  // bypassing the pre-chat form and the recovery prompt.
  const resumeSessionId = searchParams.get('session_id') || undefined;
  const resumeToken = searchParams.get('token') || undefined;

  const [session, setSession] = useState<InterviewSession | null>(null);
  const [sessionToken, setSessionToken] = useState<string | undefined>();
  const [initialMessage, setInitialMessage] = useState<string>('');
  const [recoveredMessages, setRecoveredMessages] = useState<RecoveredMessage[]>([]);
  const [isRecovered, setIsRecovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [storedSessionId, setStoredSessionId] = useState<string | null>(null);
  const [storedSessionToken, setStoredSessionToken] = useState<string | undefined>();
  const [storedTimestamp, setStoredTimestamp] = useState<number | undefined>();
  const [priorCompleted, setPriorCompleted] = useState<CompletedSessionRecord | null>(null);
  const [pendingFormData, setPendingFormData] = useState<{ name: string; email: string; referralSource?: string } | null>(null);

  useEffect(() => {
    pushPageContext('ai_discovery_intake');
  }, []);

  // Check for existing session
  useEffect(() => {
    // Widget hand-off path takes priority — no recovery prompt, no form.
    if (resumeSessionId && resumeToken) return;
    if (forceNew) return;

    const stored = getStoredSession();
    if (stored && stored.flowType === FLOW_TYPE) {
      setStoredSessionId(stored.sessionId);
      setStoredSessionToken(stored.sessionToken);
      setStoredTimestamp(stored.timestamp);
      setShowRecoveryPrompt(true);
      setShowForm(false);
    }
  }, [forceNew, resumeSessionId, resumeToken]);

  // Promote a light-mode chat-widget session into a full intake. Triggers
  // once on mount when ?session_id and ?token are both present in the URL.
  useEffect(() => {
    if (!resumeSessionId || !resumeToken) return;

    let cancelled = false;
    setShowForm(false);
    setShowRecoveryPrompt(false);
    setIsLoading(true);

    (async () => {
      try {
        const promote = await fetch(withBase(`/api/interview/session/${resumeSessionId}/promote`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: resumeToken }),
        });

        if (!promote.ok) {
          const data = await promote.json().catch(() => ({}));
          throw new Error(data?.error || 'Sessie kon niet worden voortgezet');
        }

        const result = await recoverSession(resumeSessionId, resumeToken);
        if (cancelled) return;

        if (!result.success || !result.session) {
          throw new Error('Sessie niet gevonden');
        }

        setSession(result.session as unknown as InterviewSession);
        setSessionToken(resumeToken);
        setRecoveredMessages(result.messages || []);
        setIsRecovered(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to resume widget session:', err);
        setError(err instanceof Error ? err.message : 'Er is iets misgegaan');
        setShowForm(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resumeSessionId, resumeToken]);

  // Create new session with contact info from form
  const createSession = useCallback(async (contactInfo?: { name: string; email: string; referralSource?: string }) => {
    setIsLoading(true);
    setError(null);
    setShowRecoveryPrompt(false);
    setShowForm(false);

    try {
      clearStoredSession();

      const response = await fetch(withBase('/api/interview/session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_type: FLOW_TYPE,
          referral_source: contactInfo?.referralSource || referralSource,
          referral_campaign: referralCampaign,
          contact_info: contactInfo ? { name: contactInfo.name, email: contactInfo.email } : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Sessie aanmaken mislukt');
      }

      const data = await response.json();
      pushEvent('ai_discovery_start', { referral_source: contactInfo?.referralSource || referralSource || undefined });
      setSession(data.session);
      setInitialMessage(data.initial_message);
      setSessionToken(data.session_token);
      setIsRecovered(false);
    } catch (err) {
      console.error('Failed to create session:', err);
      setError(err instanceof Error ? err.message : 'Er is iets misgegaan');
      setShowForm(true);
    } finally {
      setIsLoading(false);
    }
  }, [referralSource, referralCampaign]);

  // Handle pre-chat form submission. If a completed session for the same
  // email exists locally within 30 days, show a dedup prompt instead of
  // creating another one silently — surfaces "you already did this" to the
  // user so the sales team doesn't chase duplicate leads.
  const handleFormSubmit = useCallback(
    (data: { name: string; email: string; referralSource?: string }) => {
      const prior = findCompletedSessionByEmail(data.email);
      if (prior) {
        setPriorCompleted(prior);
        setPendingFormData(data);
        setShowForm(false);
        return;
      }
      createSession(data);
    },
    [createSession]
  );

  const handleContinueDespitePrior = useCallback(() => {
    if (!pendingFormData) return;
    setPriorCompleted(null);
    createSession(pendingFormData);
    setPendingFormData(null);
  }, [pendingFormData, createSession]);

  const handleCancelPriorPrompt = useCallback(() => {
    setPriorCompleted(null);
    setPendingFormData(null);
    setShowForm(true);
  }, []);

  // Recover existing session
  const handleRecover = useCallback(async () => {
    if (!storedSessionId) return;

    setIsLoading(true);
    setError(null);
    setShowRecoveryPrompt(false);

    try {
      const result = await recoverSession(storedSessionId, storedSessionToken);

      if (!result.success || !result.session) {
        setShowForm(true);
        return;
      }

      setSession(result.session as unknown as InterviewSession);
      setSessionToken(storedSessionToken);
      setRecoveredMessages(result.messages || []);
      setIsRecovered(true);
    } catch (err) {
      console.error('Failed to recover session:', err);
      setShowForm(true);
    } finally {
      setIsLoading(false);
    }
  }, [storedSessionId, storedSessionToken]);

  // Handle "start fresh" from recovery prompt
  const handleStartFresh = useCallback(() => {
    clearStoredSession();
    setShowRecoveryPrompt(false);
    setShowForm(true);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-off-black/60">Even geduld, je gesprek wordt gestart...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !showForm) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-off-white">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-off-black mb-2">Kan niet starten</h2>
          <p className="text-off-black/60 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-orange-cta hover:bg-orange-hover text-white rounded-lg transition-colors"
          >
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  // Recovery prompt
  if (showRecoveryPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-off-white">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-teal/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-teal"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-off-black mb-2">
            Eerder gesprek voortzetten?
          </h2>
          <p className="text-off-black/60 mb-6">
            Je hebt een onafgerond gesprek. Wil je verdergaan waar je gebleven was?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRecover}
              className="px-6 py-2 bg-orange-cta hover:bg-orange-hover text-white rounded-lg transition-colors"
            >
              Verdergaan
            </button>
            <button
              onClick={handleStartFresh}
              className="px-6 py-2 bg-off-white border border-off-black/15 hover:bg-off-black/5 text-off-black rounded-lg transition-colors"
            >
              Opnieuw beginnen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Prior-completed soft dedup prompt — shown when the submitted email
  // matches a session this device already completed in the past 30 days.
  if (priorCompleted && pendingFormData) {
    const daysAgo = Math.max(
      1,
      Math.round((Date.now() - priorCompleted.completedAt) / (24 * 60 * 60 * 1000))
    );
    const reportHref = priorCompleted.sessionToken
      ? `/intake/report?id=${priorCompleted.sessionId}&token=${priorCompleted.sessionToken}`
      : null;
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-off-white">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-teal/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-off-black mb-2">Je hebt al een gesprek gehad</h2>
          <p className="text-off-black/60 mb-6">
            {daysAgo === 1
              ? 'Je hebt gisteren al een gesprek afgerond.'
              : `Je hebt ${daysAgo} dagen geleden al een gesprek afgerond.`}{' '}
            Wil je het vorige rapport bekijken of een nieuw gesprek starten?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {reportHref && (
              <a
                href={reportHref}
                className="px-6 py-2 bg-orange-cta hover:bg-orange-hover text-white rounded-lg transition-colors"
              >
                Vorig rapport bekijken
              </a>
            )}
            <button
              onClick={handleContinueDespitePrior}
              className="px-6 py-2 bg-off-white border border-off-black/15 hover:bg-off-black/5 text-off-black rounded-lg transition-colors"
            >
              Nieuw gesprek starten
            </button>
            <button
              onClick={handleCancelPriorPrompt}
              className="px-4 py-2 text-sm text-off-black/50 hover:text-off-black/70 transition-colors"
            >
              Annuleren
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pre-chat form (shown before session creation)
  if (showForm && !session) {
    return <PreChatForm onSubmit={handleFormSubmit} isLoading={isLoading} />;
  }

  // No session
  if (!session) {
    return null;
  }

  return (
    <ChatInterface
      session={session}
      initialMessage={initialMessage}
      flowType={FLOW_TYPE}
      sessionToken={sessionToken}
      recoveredMessages={isRecovered ? recoveredMessages : undefined}
      isRecovered={isRecovered}
      recoveredStoredAt={isRecovered ? storedTimestamp : undefined}
    />
  );
}
